import { useEffect, useState, type FormEvent } from 'react';
import { Loader2 } from 'lucide-react';
import type { ConviteDto } from '@nexital/shared';
import { ErroApi, type ApiAcessoPublico } from '../../application/portas';
import { Marca } from '../design-system/Marca';
import { LogoGoogle } from './TelaEntrar';
import { TelaAviso } from './TelaAviso';

type Props = { token: string; api: ApiAcessoPublico; entrar: (email: string, senha: string) => Promise<void> };

const irParaOInicio = { rotulo: 'Ir para o Nexital', executar: () => window.location.assign('/') };

/**
 * Link do convite (RQ-ACE-003): mostra quem convidou e para qual quadro; a pessoa cria a senha (e entra na hora) ou
 * entra com o Google do mesmo e-mail, que aceita o convite sozinho.
 */
export function TelaConvite({ token, api, entrar }: Props) {
  const [convite, definirConvite] = useState<ConviteDto | null | 'carregando'>('carregando');
  useEffect(() => {
    api.consultarConvite(token).then(definirConvite, () => definirConvite(null));
  }, [api, token]);

  if (convite === 'carregando') return <TelaAviso titulo="Abrindo o convite…" />;
  if (!convite) return <TelaAviso titulo="Convite não encontrado" texto="Confira o link do e-mail ou peça um novo convite." acoes={[irParaOInicio]} />;
  if (convite.situacao === 'aceito') return <TelaAviso titulo="Convite já aceito" texto="Entre no Nexital com sua conta." acoes={[irParaOInicio]} />;
  if (convite.situacao === 'expirado') {
    return <TelaAviso titulo="Convite vencido" texto={`Peça um novo convite a ${convite.convidadoPor}.`} acoes={[irParaOInicio]} />;
  }
  return <FormularioConvite convite={convite} token={token} api={api} entrar={entrar} />;
}

function FormularioConvite({ convite, token, api, entrar }: Props & { convite: ConviteDto }) {
  const [nome, definirNome] = useState('');
  const [senha, definirSenha] = useState('');
  const [confirmacao, definirConfirmacao] = useState('');
  const [enviando, definirEnviando] = useState(false);
  const [erro, definirErro] = useState<string>();
  const senhasIguais = senha === confirmacao;

  const aoEnviar = async (evento: FormEvent) => {
    evento.preventDefault();
    definirEnviando(true);
    definirErro(undefined);
    try {
      await api.aceitarConvite(token, { nome, senha });
      await entrar(convite.email, senha);
      window.location.assign('/');
    } catch (e) {
      definirErro(e instanceof ErroApi ? e.message : 'Não foi possível aceitar agora. Tente de novo em instantes.');
      definirEnviando(false);
    }
  };

  return (
    <main className="tela-acesso">
      <form className="cartao-acesso" onSubmit={aoEnviar} noValidate>
        <div className="cartao-acesso__marca">
          <Marca tamanho={32} />
          <span>Nexital</span>
        </div>
        <h1>Convite para o quadro {convite.quadro}</h1>
        <p className="cartao-acesso__subtitulo">
          <strong>{convite.convidadoPor}</strong> convidou <strong>{convite.email}</strong> para o quadro {convite.quadro}, da {convite.empresa},
          com o perfil {convite.perfil}.
        </p>
        <Campo rotulo="Seu nome" valor={nome} mudar={definirNome} autoComplete="name" />
        <Campo rotulo="Crie uma senha (mínimo de 8 caracteres)" valor={senha} mudar={definirSenha} autoComplete="new-password" senha />
        <Campo rotulo="Repita a senha" valor={confirmacao} mudar={definirConfirmacao} autoComplete="new-password" senha />
        {confirmacao && !senhasIguais && <p className="cartao-acesso__erro">As senhas não são iguais.</p>}
        {erro && (
          <p className="cartao-acesso__erro" role="alert">
            {erro}
          </p>
        )}
        <button type="submit" className="botao-primario cartao-acesso__botao" disabled={enviando || nome.trim().length < 2 || senha.length < 8 || !senhasIguais}>
          {enviando && <Loader2 size={18} className="girando" aria-hidden />} Aceitar e entrar
        </button>
        <div className="cartao-acesso__divisor">
          <span>ou</span>
        </div>
        <a className="botao-google" href="/api/auth/google">
          <LogoGoogle /> Entrar com o Google de {convite.email}
        </a>
      </form>
    </main>
  );
}

type PropsCampo = { rotulo: string; valor: string; mudar: (v: string) => void; autoComplete: string; senha?: boolean };

function Campo({ rotulo, valor, mudar, autoComplete, senha }: PropsCampo) {
  return (
    <label className="campo">
      <span>{rotulo}</span>
      <input type={senha ? 'password' : 'text'} autoComplete={autoComplete} required value={valor} onChange={(e) => mudar(e.target.value)} />
    </label>
  );
}
